import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  ViewChild,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import {
  DropdownMenuComponent,
  DropdownMenuOption,
  DropdownMenuTriggerDirective,
} from '@serious-system';
import { environment } from '../../environments/environment';
import { ADMIN_ENTRY_FROM_STATE_KEY } from '../admin/admin-analytics.helpers';
import { AnalyticsService } from '../analytics/analytics.service';
import * as fromAuth from '../auth';
import { selectFavoriteAssistants } from '../favorites/favorites.selectors';
import * as fromFeatureFlags from '../feature-flags';
import * as fromLayout from '../layout';
import { JiraFeedbackDirective } from '../layout/sidebar/jira-feedback.directive';
import * as fromApp from '../store/app.reducer';
import { ChatsHelper } from './chats.helper';
import { ChatNavigationItem } from './chats.interfaces';
import { ChatAssistantProfileComponent } from './components/chat-assistant-profile.component';
import { DocumentViewerSmartComponent } from './components/document-viewer/document-viewer.smart.component';
import { NavigationMinimizedSmartComponent } from './components/navigation/navigation-minimized.smart.component';
import { NavigationSmartComponent } from './components/navigation/navigation.smart.component';
import * as fromShared from './shared';
import { ChatActions, chatsFeature, ChatsState } from './store';

@Component({
  selector: 'squadbox-chats-desktop',
  imports: [
    RouterModule,
    TranslateModule,
    NgClass,
    DocumentViewerSmartComponent,
    ChatAssistantProfileComponent,
    NavigationSmartComponent,
    DropdownMenuComponent,
    DropdownMenuTriggerDirective,
    JiraFeedbackDirective,
    MatTooltipModule,
    NavigationMinimizedSmartComponent,
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseNavigationIconDirective,
    fromSeriousSystem.UseArrowIconDirective,
    fromSeriousSystem.AvatarDirective,
    fromFeatureFlags.FeatureFlagsDirective,
  ],
  providers: [ChatsHelper],
  template: `
    <div class="flex h-full overflow-hidden bg-gradient-e-vertical">
      <div
        class="relative min-h-0"
        [class.grow]="viewMode() !== 'conversation'"
      >
        <!-- Document viewer -->
        @if (viewMode().includes('doc-viewer')) {
          <squadbox-document-viewer-smart></squadbox-document-viewer-smart>
          <!-- Navigation Desktop -->
        } @else if (!isNavigationMinimized()) {
          <div
            #navigationContainer
            [class.hidden]="viewMode().includes('doc-viewer')"
            class="
              absolute z-40
              flex flex-col h-screen overflow-hidden w-70
              border-r-neutral-200 bg-shades-white
              transition-opacity duration-500 [view-transition-name:navigation]
              shadow
            "
            animate.enter="fade-in"
            animate.leave="fade-out"
          >
            <!-- Navigation topbar -->
            <div class="flex items-center justify-between pt-4 px-4">
              <!-- Navigation toggle collapsed fabs -->
              <button
                sdIconButton
                variant="icon"
                color="neutral"
                size="md"
                (click)="handleClickNavigationMinimizeCTA()"
              >
                <svg sdUseNavigationIcon="sidebar"></svg>
              </button>
              <!-- Navigation new chat -->
              <button
                sdIconButton
                variant="icon"
                color="primary"
                size="md"
                (click)="handleClickNewChatCTA()"
              >
                <svg sdUseNavigationIcon="new-chat" class="size-6"></svg>
              </button>
            </div>
            <!-- Navigation threads -->
            <squadbox-navigation-smart
              class="flex-1 overflow-x-hidden"
              (chatDeleteClicked)="chatDeleteClicked.emit($event)"
            ></squadbox-navigation-smart>
            <!-- Navigation footer -->
            @let user = currentUser();
            <button
              class="
                  flex items-center gap-4 p-4
                  border-t border-t-neutral-200
                  hover:bg-neutral-50 transition-colors
                "
              [sdDropdownMenuTrigger]="profileDropdownMenu"
              [cdkMenuPosition]="[
                {
                  originX: 'end',
                  originY: 'top',
                  overlayX: 'start',
                  overlayY: 'top',
                  offsetX: -10,
                  offsetY: -20,
                },
              ]"
            >
              <div
                sdAvatar
                size="md"
                color="light"
                class="pointer-events-none font-semibold"
              >
                {{ !user ? '...' : (user.initials ?? '-') }}
              </div>
              <span class="typo-p3 flex-1 text-left font-semibold truncate">{{
                !user
                  ? '...'
                  : (user.fullName ?? ('USER.NOT_SPECIFIED' | translate))
              }}</span>
              <svg sdUseArrowIcon="chevron-right" class="size-4 shrink-0"></svg>
            </button>
            <sd-dropdown-menu
              #profileDropdownMenu
              [options]="navigationFooterOptions()"
              [responsive]="false"
            ></sd-dropdown-menu>
          </div>
        } @else {
          <squadbox-navigation-minimized-smart />
        }
      </div>

      <!-- Conversation -->
      @if (viewMode() !== 'doc-viewer-assistant-profile') {
        <!-- Sliding panel (For smooth content shift on sidebar expansion/retraction) -->
        <div
          class="transition-all duration-500 shrink-0"
          [class.hidden]="viewMode().includes('doc-viewer')"
          [ngClass]="{
            'w-70': !isNavigationMinimized(),
            'w-0': isNavigationMinimized(),
          }"
        ></div>
        <div
          class="grow h-full overflow-hidden"
          [ngClass]="{
            'max-w-100 w-100': viewMode() === 'doc-viewer-conversation',
          }"
        >
          <div class="h-full w-full overflow-hidden">
            <router-outlet></router-outlet>
          </div>
        </div>
      }

      <!-- Assistant profile: only render when the selected chat has an assistant -->
      @let assistant = selectedChatAssistant();
      <div
        class="w-70 h-full shrink-0 overflow-hidden border-l border-neutral-200 transition-all duration-500 ease-in-spring-15"
        [class.-mr-70]="!assistantProfileOpened() || !assistant"
      >
        @if (assistant) {
          @defer (when assistantProfileOpened()) {
            <squadbox-chat-assistant-profile
              [selectedAssistant]="assistant"
              (chatDeleteClicked)="chatDeleteClicked.emit($event)"
            ></squadbox-chat-assistant-profile>
          }
        }
      </div>
    </div>
    <div
      #jiraFeedbackContainer
      squadboxFeatureFlag="ff-bug-reporting-button-jira"
      squadboxJiraFeedback
      hidden
    ></div>
  `,
})
export class ChatsDesktopResponsive implements OnInit {
  private readonly routerService = inject(fromShared.RouterService);
  private readonly router = inject(Router);
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  private readonly assistantsStore = inject(Store);
  private readonly layoutStore = inject<Store<fromLayout.LayoutState>>(Store);
  private readonly featureFlagsStore = inject(
    Store<fromFeatureFlags.FeatureFlagsState>,
  );
  private readonly chatsHelper = inject(ChatsHelper);
  private readonly authStore = inject(Store<fromAuth.AuthState>);
  private readonly appStore = inject(Store<fromApp.AppState>);
  private readonly mobileNavigation = inject(
    fromShared.MobileNavigationService,
  );
  private readonly mobileSidebar = inject(fromShared.MobileSidebarService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly uiReleaseVersionWithoutPrefix = this.appStore.selectSignal(
    fromApp.appFeature.selectUiReleaseVersionWithoutPrefix,
  );

  @ViewChild('jiraFeedbackContainer')
  jiraFeedbackContainer!: ElementRef<HTMLDivElement>;

  public readonly translateService = inject(TranslateService);
  public readonly isNewChat = input.required<boolean>();
  private readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );
  public readonly chatNavigationItems = input.required<ChatNavigationItem[]>();
  public readonly chatDeleteClicked = output<string>();
  public readonly selectedChat = computed(() => {
    const selectedChat = this.chatsStore.selectSignal(
      chatsFeature.selectSelectedChat,
    );

    return {
      ...selectedChat(),
      title: this.chatsHelper.getChatTitle(selectedChat()?.title),
    };
  });
  public readonly selectedChatAssistant = this.assistantsStore.selectSignal(
    fromShared.selectSkillShown,
  );
  public readonly isNavigationMinimized = this.chatsStore.selectSignal(
    chatsFeature.selectIsNavigationMinimized,
  );
  public readonly activeAssistants = this.assistantsStore.selectSignal(
    selectFavoriteAssistants,
  );
  public readonly viewMode = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectViewMode,
  );
  public readonly assistantProfileOpened = this.chatsStore.selectSignal(
    chatsFeature.selectIsAssistantProfileOpened,
  );
  public readonly ffBugReportingButtonJira =
    this.featureFlagsStore.selectSignal(
      fromFeatureFlags.featureFlagsFeature.selectFeatureFlag(
        'ff-bug-reporting-button-jira',
      ),
    );
  public readonly currentUser = this.authStore.selectSignal(
    fromAuth.authFeature.selectUser,
  );
  private readonly isAdminOrAbove = this.authStore.selectSignal(
    fromAuth.authFeature.selectIsAdminOrAbove,
  );
  public readonly navigationFooterOptions = computed<DropdownMenuOption[]>(
    () => {
      const options: DropdownMenuOption[] = [
        // SB-6520: one Settings window replaces the Language and Connectors items (KD-1).
        {
          label: this.translateService.instant('HOME.SETTINGS') as string,
          action: this.onTriggerAction('settings'),
        },
        ...(this.isAdminOrAbove()
          ? [
              {
                label: this.translateService.instant('HOME.ADMIN') as string,
                action: this.onTriggerAction('org_settings'),
              },
            ]
          : []),
        {
          label: this.translateService.instant('HOME.HELP_CENTER') as string,
          action: this.onTriggerAction('help_center'),
        },
        {
          label: this.translateService.instant(
            'HOME.TERMS_AND_CONDITIONS',
          ) as string,
          action: this.onTriggerAction('terms_and_conditions'),
        },
      ];

      if (this.ffBugReportingButtonJira()) {
        options.push({
          label: this.translateService.instant('SIDEBAR.REPORT_BUG') as string,
          action: this.onTriggerAction('report_bug'),
        });
      }

      options.push(
        {
          label: this.translateService.instant('HOME.LOGOUT') as string,
          action: this.onTriggerAction('logout'),
          separator: true,
        },
        {
          label: `${this.translateService.instant('HOME.VERSION')} ${this.uiReleaseVersionWithoutPrefix()}`,
          disabled: true,
          separator: true,
        },
      );

      return options;
    },
  );

  public ngOnInit() {
    if (this.isMobile()) {
      return;
    }
    if (this.mobileNavigation.isQueryParamSet()) {
      void this.mobileNavigation.close();
    }
    if (this.mobileSidebar.isQueryParamSet()) {
      void this.mobileSidebar.close();
    }
  }

  public handleClickNavigationMinimizeCTA() {
    this.chatsStore.dispatch(ChatActions.minimizeNavigation());
  }

  public async handleClickNewChatCTA() {
    await this.routerService.navigateToNewChat();
  }

  private onTriggerAction(
    action:
      | 'settings'
      | 'org_settings'
      | 'help_center'
      | 'terms_and_conditions'
      | 'report_bug'
      | 'logout',
  ) {
    return () => {
      switch (action) {
        case 'org_settings':
          void this.router.navigate(['/admin'], {
            state: { [ADMIN_ENTRY_FROM_STATE_KEY]: 'user_menu' },
          });
          break;
        case 'settings':
          this.appStore.dispatch(
            fromLayout.LayoutActions.openSettings({
              source: 'desktop-navigation-footer',
            }),
          );
          break;
        case 'help_center':
          this.analyticsService
            .sendHelpCenterOpenedEvent({ source: 'desktop-navigation-footer' })
            .subscribe();
          window.open(
            this.translateService.getCurrentLang() === 'fr'
              ? environment.appHelpCenterUrl.fr
              : environment.appHelpCenterUrl.en,
            '_blank',
          );
          break;
        case 'terms_and_conditions':
          this.analyticsService
            .sendTermsOpenedEvent({ source: 'desktop-navigation-footer' })
            .subscribe();
          window.open(
            this.translateService.getCurrentLang() === 'fr'
              ? environment.appTermsAndConditionsUrl.fr
              : environment.appTermsAndConditionsUrl.en,
            '_blank',
          );
          break;
        case 'report_bug':
          this.jiraFeedbackContainer.nativeElement.click();
          break;
        case 'logout':
          this.authStore.dispatch(fromAuth.AuthActions.logout());
          break;
      }
    };
  }
}
